/**
 * Component Registry for Dietz Property Group Theme
 * 
 * Extends the base prestige theme registry with Dietz Property Group specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-prestige/registry';
import { Header } from './components/Header';
import { ApplyNow } from './components/ApplyNow';
import { NudgeCTA } from './components/NudgeCTA';
import { ApartmentSyncSection } from './components/ApartmentSyncSection';
import { PropertyOverview } from './components/PropertyOverview';

/**
 * Dietz Property Group specific component overrides.
 * These components have been customized for the Dietz Property Group brand.
 * 
 * Example:
 * import { Footer } from './components/Footer';
 * 
 * const dietzpropertygroupOverrides: Partial<ComponentRegistry> = {
 *   Footer,
 * };
 */
const dietzpropertygroupOverrides: Partial<ComponentRegistry> = {
  // Header: copy of the prestige Header with the Knock Doorway tour-scheduling
  // popup intercept wired onto the "Schedule a Tour" link.
  Header,
  // ApplyNow: replaces the prestige two-card layout with the RealPage
  // Online Leasing (OLL) block embedded directly in the section.
  ApplyNow,
  // NudgeCTA: optional "call us to schedule a tour" bar rendered under the Hero.
  // Hidden by default; opt-in per property via the nudgecta.enabled toggle token.
  NudgeCTA,
  // ApartmentSyncSection: copy of the prestige wrapper that falls back to the
  // default `floorplans` block when the ApartmentSync content tokens
  // (javascript_id / config_id) aren't configured for the property.
  ApartmentSyncSection,
  // PropertyOverview: copy of the prestige section that swaps the title to
  // "Affordable apartments in [City, State]" for properties flagged with the
  // custom_tax_credit_properties custom column; otherwise unchanged.
  PropertyOverview,
};

/**
 * Final merged registry for Dietz Property Group properties.
 * Inherits all components from the prestige theme, with Dietz Property Group overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...dietzpropertygroupOverrides,
};

export default registry;
